/**
 * InviteMandantSection — Cockpit-Sektion für den Berater zur Einladung
 * eines Mandanten.
 *
 * Drei Anzeigezustände:
 *  1. Idle: Email-Input + „Einladung senden →"
 *  2. Pending: aktive (nicht eingelöste) Einladung sichtbar mit „Erneut senden"
 *     und „Widerrufen"
 *  3. Active: Mandant hat den Zugang aktiviert → Status + „Zugang entziehen"
 *
 * Wir laden parallel aktive Einladungen UND aktiven Mandanten-Zugang. Wenn
 * beides existiert (theoretischer Übergang während der Einlösung), priorisiert
 * der „Active"-Zustand.
 *
 * DSGVO-Hinweis: explizit im Render-Output als prominenter Block, sodass der
 * Berater versteht, was der Mandant sehen kann.
 */

import { useCallback, useEffect, useState } from 'react';
import { Mail, ShieldCheck, X, RefreshCw } from 'lucide-react';
import { de } from '../../lib/i18n/de';
import { useConfirm } from '../ui/ConfirmDialog';
import { Button } from '../ui/Button';
import {
  createInvitation,
  listInvitationsForCase,
  listMandantAccessForCase,
  revokeInvitation,
  revokeMandantAccess,
  sendMandantInvitationEmail,
  type CaseMandantAccess,
  type MandantInvitation,
} from '../../lib/supabaseInvitations';

interface InviteMandantSectionProps {
  readonly caseId: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; invitations: MandantInvitation[]; access: CaseMandantAccess[] }
  | { kind: 'error'; message: string };

type ActionState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'revoking' }
  | { kind: 'error'; message: string }
  | { kind: 'success'; message: string };

export function InviteMandantSection({ caseId }: InviteMandantSectionProps) {
  const confirm = useConfirm();
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [action, setAction] = useState<ActionState>({ kind: 'idle' });
  const [emailInput, setEmailInput] = useState('');

  const refresh = useCallback(async () => {
    setLoad({ kind: 'loading' });
    try {
      const [invs, accs] = await Promise.all([
        listInvitationsForCase(caseId),
        listMandantAccessForCase(caseId),
      ]);
      setLoad({ kind: 'ready', invitations: invs, access: accs });
    } catch (err) {
      setLoad({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Fehler beim Laden.',
      });
    }
  }, [caseId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // -- Invariants aus dem Datenstand ableiten --

  const activeAccess =
    load.kind === 'ready' ? (load.access.find((a) => a.revoked_at === null) ?? null) : null;

  const pendingInvitation =
    load.kind === 'ready'
      ? (load.invitations.find(
          (i) =>
            i.redeemed_at === null && i.revoked_at === null && new Date(i.expires_at) > new Date(),
        ) ?? null)
      : null;

  // -- Action-Handler --

  async function handleSend() {
    setAction({ kind: 'sending' });
    try {
      const inv = await createInvitation({ caseId, mandantEmail: emailInput });
      await sendMandantInvitationEmail(inv.mandant_email, inv.token);
      setAction({ kind: 'success', message: `Einladung an ${inv.mandant_email} versendet.` });
      setEmailInput('');
      await refresh();
    } catch (err) {
      setAction({
        kind: 'error',
        message: err instanceof Error ? err.message : de.invitations.errorSendFailed,
      });
    }
  }

  async function handleResend(inv: MandantInvitation) {
    setAction({ kind: 'sending' });
    try {
      await sendMandantInvitationEmail(inv.mandant_email, inv.token);
      setAction({
        kind: 'success',
        message: `Einladungs-Mail erneut an ${inv.mandant_email} versendet.`,
      });
    } catch (err) {
      setAction({
        kind: 'error',
        message: err instanceof Error ? err.message : de.invitations.errorSendFailed,
      });
    }
  }

  async function handleRevokeInvitation(invId: string) {
    setAction({ kind: 'revoking' });
    try {
      await revokeInvitation(invId);
      setAction({ kind: 'success', message: 'Einladung widerrufen.' });
      await refresh();
    } catch (err) {
      setAction({
        kind: 'error',
        message: err instanceof Error ? err.message : de.invitations.errorRevokeFailed,
      });
    }
  }

  async function handleRevokeAccess(accessId: string) {
    const proceed = await confirm({
      title: de.invitations.confirmRevokeAccessTitle,
      body: de.invitations.confirmRevokeAccess,
      confirmLabel: de.invitations.confirmRevokeAccessConfirm,
      tone: 'danger',
    });
    if (!proceed) return;
    setAction({ kind: 'revoking' });
    try {
      await revokeMandantAccess(accessId);
      setAction({ kind: 'success', message: 'Zugang entzogen.' });
      await refresh();
    } catch (err) {
      setAction({
        kind: 'error',
        message: err instanceof Error ? err.message : de.invitations.errorAccessRevokeFailed,
      });
    }
  }

  // -- Render --

  return (
    <section className="mb-10 rounded-sm border border-stone-200 bg-white">
      <div className="border-b border-stone-200 px-6 py-5 md:px-10">
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {de.invitations.sectionLabel}
        </p>
        <h2 className="mt-2 font-serif text-2xl font-bold tracking-tight text-stone-900">
          {de.invitations.sectionTitle}
        </h2>
        <p className="mt-2 max-w-prose font-serif text-sm italic text-stone-500">
          {de.invitations.sectionIntro}
        </p>
      </div>

      <div className="px-6 py-6 md:px-10">
        {load.kind === 'loading' && (
          <p className="font-serif text-sm italic text-stone-500">Lade Einladungs-Status…</p>
        )}
        {load.kind === 'error' && (
          <div className="rounded-sm border border-red-200 bg-red-50 px-4 py-3">
            <p className="font-sans text-xs font-medium uppercase tracking-widest text-red-800">
              Fehler
            </p>
            <p className="mt-1 font-serif text-sm text-red-900">{load.message}</p>
          </div>
        )}

        {load.kind === 'ready' && (
          <>
            {/* Zustand 3: Mandant hat Zugang */}
            {activeAccess && (
              <div className="flex items-start gap-3 rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3">
                <ShieldCheck size={18} className="mt-0.5 flex-shrink-0 text-emerald-700" />
                <div className="flex-1">
                  <p className="font-sans text-xs font-medium uppercase tracking-widest text-emerald-800">
                    Zugang aktiv
                  </p>
                  <p className="mt-1 font-serif text-sm text-emerald-900">
                    {de.invitations.statusRedeemed
                      .replace(
                        '{email}',
                        // mandant_user_id zeigt nicht die Email — wir holen sie aus
                        // der zugehörigen Einladung (mandant_email) wenn verfügbar.
                        load.invitations.find(
                          (i) => i.id === activeAccess.granted_via_invitation_id,
                        )?.mandant_email ?? '—',
                      )
                      .replace(
                        '{date}',
                        new Date(activeAccess.granted_at).toLocaleDateString('de-DE'),
                      )}
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      void handleRevokeAccess(activeAccess.id);
                    }}
                    disabled={action.kind === 'revoking'}
                    className="mt-3"
                  >
                    {de.invitations.revokeAccessButton}
                  </Button>
                </div>
              </div>
            )}

            {/* Zustand 2: aktive Einladung wartet auf Klick */}
            {!activeAccess && pendingInvitation && (
              <div className="flex items-start gap-3 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3">
                <Mail size={18} className="mt-0.5 flex-shrink-0 text-amber-700" />
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-xs font-medium uppercase tracking-widest text-amber-800">
                    Einladung versendet
                  </p>
                  <p className="mt-1 font-serif text-sm text-amber-900">
                    {de.invitations.statusInvitedAt
                      .replace('{email}', pendingInvitation.mandant_email)
                      .replace(
                        '{date}',
                        new Date(pendingInvitation.created_at).toLocaleDateString('de-DE'),
                      )}{' '}
                    ·{' '}
                    {de.invitations.statusValidUntil.replace(
                      '{date}',
                      new Date(pendingInvitation.expires_at).toLocaleDateString('de-DE'),
                    )}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        void handleResend(pendingInvitation);
                      }}
                      disabled={action.kind === 'sending'}
                      className="inline-flex items-center gap-2 rounded-sm border border-amber-700 bg-white px-3 py-2 font-sans text-xs font-medium uppercase tracking-widest text-amber-900 hover:bg-amber-700 hover:text-white disabled:opacity-40"
                    >
                      <RefreshCw size={12} />
                      {de.invitations.resendButton}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        void handleRevokeInvitation(pendingInvitation.id);
                      }}
                      disabled={action.kind === 'revoking'}
                      className="inline-flex items-center gap-2 rounded-sm border border-stone-300 bg-white px-3 py-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 hover:border-stone-900 hover:text-stone-900 disabled:opacity-40"
                    >
                      <X size={12} />
                      {de.invitations.revokeButton}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Zustand 1: nichts aktiv → Formular */}
            {!activeAccess && !pendingInvitation && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleSend();
                }}
                className="space-y-3"
              >
                <label
                  htmlFor="invite-email"
                  className="block font-sans text-xs font-medium uppercase tracking-widest text-stone-500"
                >
                  {de.invitations.emailLabel}
                </label>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <input
                    id="invite-email"
                    type="email"
                    required
                    value={emailInput}
                    onChange={(e) => {
                      setEmailInput(e.target.value);
                    }}
                    placeholder={de.invitations.emailPlaceholder}
                    className="flex-1 rounded-sm border border-stone-200 bg-white px-4 py-3 font-serif text-base text-stone-900 placeholder:italic placeholder:text-stone-400 focus:border-stone-900 focus:outline-none focus:ring-0"
                  />
                  <Button
                    type="submit"
                    disabled={action.kind === 'sending' || emailInput.length === 0}
                  >
                    {action.kind === 'sending'
                      ? de.invitations.sendingButton
                      : de.invitations.sendButton}
                  </Button>
                </div>
              </form>
            )}

            {/* Generic action feedback */}
            {action.kind === 'success' && (
              <p className="mt-4 rounded-sm bg-emerald-50 px-3 py-2 font-sans text-xs text-emerald-800">
                ✓ {action.message}
              </p>
            )}
            {action.kind === 'error' && (
              <p className="mt-4 rounded-sm bg-red-50 px-3 py-2 font-sans text-xs text-red-800">
                {action.message}
              </p>
            )}

            <div className="mt-5 rounded-sm border border-stone-200 bg-stone-50 px-4 py-3">
              <p className="font-sans text-[11px] uppercase tracking-widest text-stone-500">
                Hinweis
              </p>
              <p className="mt-1 font-serif text-xs italic text-stone-600">
                {de.invitations.dsgvoNote}
              </p>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
